import { RouteRecordRaw } from 'vue-router';
import UploadFoldersPage from '@/VueComponents/PageComponents/UploadFolders/UploadFoldersPage.vue';
import UploadFolderDetailsPageLoader from '@/VueComponents/PageComponents/UploadFolderDetails/UploadFolderDetailsPageLoader.vue';
import RequestsPage from '@/VueComponents/Requests/RequestsPage.vue';
import CamerasPage from '@/VueComponents/PageComponents/Cameras/CamerasPage.vue';
import CameraDetailsPage from '@/VueComponents/PageComponents/CameraDetails/CameraDetailsPage.vue';
import AddCameraPage from '@/VueComponents/PageComponents/AddCamera/AddCameraPage.vue';
import GroupsPage from '@/VueComponents/PageComponents/Groups/GroupsPage.vue';
import GroupDetails from '@/VueComponents/Groups/GroupDetails.vue';
import UsersPage from '@/VueComponents/PageComponents/Users/UsersPage.vue';
import UserDetails from '@/VueComponents/PageComponents/Users/UserDetails.vue';
import MyAccountPage from '@/VueComponents/PageComponents/MyAccount/MyAccountPage.vue';
import RequestDetailsPageLoader from '@/VueComponents/PageComponents/RequestDetails/RequestDetailsPageLoader.vue';
import PortalSettingsProvider from '@/Utils/portalSettingsProvider';
import { AccountType } from '@/Types/Enums/accountType';
import { PreviousRoute } from './previousRoute';
import PortalError from '@/VueComponents/PageComponents/Portal/PortalError.vue';
import PageNotAvailable from '@/VueComponents/PageComponents/Portal/PageNotAvailable.vue';
import { useContextDataStore } from '@/VueCore/stores/contextDataStore';
import { PortalStartupErrorType } from '@/Types/Enums/portalStartupErrorType';
import constants from '@/constants';

const pageMode = constants.pageMode;

const routes: Array<RouteRecordRaw> = [
  {
    path: '/',
    name: 'Home',
    component: RequestsPage,
    meta: { helpId: '52601.htm', pageTitleKey: 'Requests' },
    props: route => ({
      previousRoute: route.params.previousRoute as PreviousRoute
    })
  },
  {
    path: '/requests',
    name: 'Requests',
    component: RequestsPage,
    meta: { helpId: '52601.htm', pageTitleKey: 'Requests' },
    props: route => ({
      previousRoute: route.params.previousRoute as PreviousRoute
    })
  },
  {
    path: '/request/:id',
    name: 'RequestDetails',
    component: RequestDetailsPageLoader,
    meta: { helpId: '52601.htm', pageTitleKey: 'RequestDetails' }
  },
  {
    path: '/uploadfolders',
    name: 'UploadFolders',
    component: UploadFoldersPage,
    meta: { helpId: '61542.htm', pageTitleKey: 'UploadFolders' },
    beforeEnter: (to, from, next) => {
      if (PortalSettingsProvider.getAccountType() !== AccountType.Partner || !PortalSettingsProvider.getSelfResponseEnabled()) {
        next('/');
        return;
      }

      next();
    },
    props: route => ({
      previousRoute: route.params.previousRoute
    })
  },
  {
    path: '/uploadfolderdetails/:id',
    name: 'UploadFolderDetails',
    component: UploadFolderDetailsPageLoader,
    meta: { helpId: '61545.htm', pageTitleKey: 'SelfResponseDetails' },
    beforeEnter: (to, from, next) => {
      if (PortalSettingsProvider.getAccountType() !== AccountType.Partner || !PortalSettingsProvider.getSelfResponseEnabled()) {
        next('/');
        return;
      }

      next();
    }
  },
  {
    path: '/cameras',
    name: 'Cameras',
    component: CamerasPage,
    meta: { helpId: '52604.htm', pageTitleKey: 'CamerasTitle' },
    beforeEnter: async (to, from, next) => {
      if (PortalSettingsProvider.getAccountType() !== AccountType.Business) {
        next('/');
        return;
      }

      next();
    },
    props: route => ({
      previousRoute: route.params.previousRoute as PreviousRoute
    })
  },
  {
    path: '/cameras/new',
    name: 'AddCamera',
    component: AddCameraPage,
    meta: { helpId: '52605.htm', pageTitleKey: 'AddCameraTitle' },
    beforeEnter: async (to, from, next) => {
      if (PortalSettingsProvider.getAccountType() !== AccountType.Business) {
        next('/');
        return;
      }

      next();
    }
  },
  {
    path: '/cameras/:id',
    name: 'EditCamera',
    component: CameraDetailsPage,
    meta: { helpId: '52639.htm', pageTitleKey: 'EditCameraTitle' },
    beforeEnter: async (to, from, next) => {
      if (PortalSettingsProvider.getAccountType() !== AccountType.Business) {
        next('/');
        return;
      }

      next();
    }
  },
  {
    path: '/users',
    name: 'Users',
    component: UsersPage,
    meta: { helpId: '57804.htm', pageTitleKey: 'UsersTitle' },
    props: {
      pageMode: pageMode.edit
    },
    beforeEnter: async (to, from, next) => {
      const contextData = useContextDataStore();
      if (!contextData.userData?.isBusinessAdminUser) {
        next('/');
        return;
      }

      next();
    }
  },
  {
    path: '/users/new',
    name: 'AddUser',
    component: UserDetails,
    meta: { helpId: '57809.htm', pageTitleKey: 'AddUserTitle' },
    props: {
      pageMode: pageMode.add
    },
    beforeEnter: async (to, from, next) => {
      const contextData = useContextDataStore();
      if (!contextData.userData?.isBusinessAdminUser) {
        next('/');
        return;
      }

      next();
    }
  },
  {
    path: '/groups',
    name: 'Groups',
    component: GroupsPage,
    meta: { helpId: '57806.htm', pageTitleKey: 'GroupsTitle' },
    beforeEnter: async (to, from, next) => {
      const contextData = useContextDataStore();
      if (!contextData.userData?.isBusinessAdminUser) {
        next('/');
        return;
      }

      next();
    }
  },
  {
    path: '/groups/new',
    name: 'AddGroup',
    component: GroupDetails,
    meta: { helpId: '57817.htm', pageTitleKey: 'AddGroupTitle' },
    props: {
      pageMode: pageMode.add
    },
    beforeEnter: async (to, from, next) => {
      const contextData = useContextDataStore();
      if (!contextData.userData?.isBusinessAdminUser) {
        next('/');
        return;
      }

      next();
    }
  },
  {
    path: '/my-account',
    name: 'MyAccount',
    component: MyAccountPage,
    meta: { helpId: '52772.htm', pageTitleKey: 'MyAccount' }
  },
  {
    path: '/error',
    name: 'PortalError',
    component: PortalError,
    meta: { helpId: '52601.htm', pageTitleKey: 'PortalError' },
    beforeEnter: (to, from, next) => {
      const contextData = useContextDataStore();
      if (contextData.portalStartupError === PortalStartupErrorType.None) {
        next('/');
      } else {
        next();
      }
    }
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotAvailable',
    component: PageNotAvailable
  }
];

export default routes;

